import { Link } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface Props {
  id: string;
  to: string;
  icon: string;
  label: string;
  active: boolean;
  onNavigate?: () => void;
  badge?: number;
}

/**
 * Élément de navigation (app épinglée) réordonnable par glisser-déposer.
 */
export default function SortableNavApp({ id, to, icon, label, active, onNavigate, badge = 0 }: Props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group/app">
      <Link
        to={to}
        onClick={onNavigate}
        className={`relative w-full flex items-center gap-3 pl-3 pr-7 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
          active
            ? 'bg-primary-50 text-primary-700 font-semibold'
            : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
        }`}
      >
        {active && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-primary-500"></span>
        )}
        <i className={`${icon} text-lg w-5 h-5 flex items-center justify-center flex-shrink-0`}></i>
        <span className="leading-snug truncate">{label}</span>
        {badge > 0 && (
          <span className="ml-auto min-w-[18px] h-[18px] px-1.5 flex items-center justify-center rounded-full bg-primary-500 text-white text-[10px] font-semibold leading-none">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </Link>

      {/* Poignée de glisser-déposer */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={(e) => e.preventDefault()}
        title="Glisser pour réordonner"
        aria-label={`Réordonner ${label}`}
        className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded text-foreground-300 hover:text-foreground-700 hover:bg-background-200/70 cursor-grab active:cursor-grabbing opacity-0 group-hover/app:opacity-100 transition-opacity touch-none"
      >
        <i className="ri-drag-move-2-line text-sm"></i>
      </button>
    </div>
  );
}